/**
 * web/src/components/gates/BatchEmailForm.tsx
 * The email-edit form for ONE speaker gate inside the Batch Review stack —
 * a focused extract of GateReview's email branch. It reuses EmailPreview
 * (the same to/cc/bcc/subject/body + attachments editor + placeholder banner)
 * so each speaker's email is individually editable, but deliberately drops the
 * card-gate / ops-thread / localStorage-draft machinery that only the
 * per-speaker page needs — a batch release only ever opens plain email gates
 * (logistics / thank_you / confirmation).
 *
 * It is a controlled leaf: the parent (the Approvals ReviewQueue) owns navigation and
 * calls the existing single-gate approve/skip endpoints. This component surfaces
 * the current edits via `onEditsChange` (so the parent can submit them) and the
 * live placeholder count via `onPlaceholdersChange` (so the parent can block
 * "Approve & Next" until they're filled — mirroring GateReview's server guard).
 *
 * Each mounted instance is keyed by workflowId+stepIndex in the parent, so
 * switching speakers remounts with a fresh form seeded from that gate's email.
 */
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { EmailPreview, type PinnedAttachment } from './EmailPreview';
import { Select } from '../shared/Select';
import { substituteSenderName } from '../../lib/senderName';
import { uniqueSpeakerPlaceholders } from '../../lib/speakerPlaceholders';
import type { GmailConnectionInfo } from '../../api/types';

/** The gate email envelope this form edits (from email-gate gateData.email). */
export interface BatchEmailSeed {
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
}

/** The editable values the form emits back to the parent for the approve payload. */
export interface BatchEmailEdits extends Record<string, unknown> {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  sendFromConnectionId: string;
  attachments: unknown[];
  /** Drive ids of pinned files the reviewer took off (sponsor welcome logos). */
  removedPinnedAttachments?: string[];
}

/**
 * Editable email form for one batch gate.
 *
 * @param seed the gate's composed email envelope (to/cc/bcc/subject/body)
 * @param approverName logged-in reviewer's name — substituted into the body's
 *        `[Your Name]` up-front so the preview matches what the server sends
 * @param healthyConnections the reviewer's non-tombstoned Gmail connections
 * @param defaultSendFromId pre-selected send-from (per-event default → first healthy)
 * @param pinnedAttachments server-attached files shown as non-removable chips
 *        (e.g. the promotion gate's monday-board speaker card, with a thumbnail)
 * @param onEditsChange called with the live edits object on every change
 * @param onPlaceholdersChange called with the live unfilled `[bracket]` tokens
 * @sideEffects owns an RHF form; watches its values
 * @idempotent yes — same seed produces the same initial form
 */
export function BatchEmailForm({
  seed,
  approverName,
  healthyConnections,
  defaultSendFromId,
  pinnedAttachments,
  onEditsChange,
  onPlaceholdersChange,
}: {
  seed: BatchEmailSeed;
  approverName: string | null | undefined;
  healthyConnections: GmailConnectionInfo[];
  defaultSendFromId: string;
  pinnedAttachments?: PinnedAttachment[];
  onEditsChange: (edits: BatchEmailEdits) => void;
  onPlaceholdersChange: (placeholders: string[]) => void;
}) {
  const defaultValues = useMemo<BatchEmailEdits>(
    () => ({
      to: seed.to,
      cc: seed.cc ?? '',
      bcc: seed.bcc ?? '',
      subject: seed.subject,
      // Mirror GateReview: swap the reviewer's FIRST name into every
      // `[Your Name]` so the preview matches the server's onApproval replace.
      body: substituteSenderName(seed.body, approverName),
      sendFromConnectionId: defaultSendFromId,
      attachments: [],
      removedPinnedAttachments: [],
    }),
    // Seed is stable per mount (the parent keys this component per gate); the
    // approver/default only resolve once, so recomputing on their change is safe.
    [seed, approverName, defaultSendFromId],
  );

  const form = useForm<BatchEmailEdits>({ defaultValues, mode: 'onSubmit' });

  // Re-seed sendFromConnectionId once the connections resolve (starts '').
  useEffect(() => {
    if (defaultSendFromId && !form.getValues('sendFromConnectionId')) {
      form.setValue('sendFromConnectionId', defaultSendFromId);
    }
  }, [defaultSendFromId, form]);

  // Reactive full-form watch — re-renders this leaf on any field edit. The
  // whole object is the source of truth for the parent's approve payload and
  // the placeholder guard, so pushing it up in an effect keeps them in lockstep.
  const values = form.watch();

  useEffect(() => {
    onEditsChange(form.getValues());
    onPlaceholdersChange(
      uniqueSpeakerPlaceholders(String(values.subject ?? ''), String(values.body ?? '')),
    );
    // `values` identity changes on every edit; form.getValues() reads the live
    // snapshot so the parent always gets the current envelope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, onEditsChange, onPlaceholdersChange]);

  const fromAddress =
    healthyConnections.find((c) => c.id === values.sendFromConnectionId)?.email ??
    healthyConnections[0]?.email ??
    '(connect Gmail to send)';

  return (
    <FormProvider {...form}>
      <EmailPreview
        from={fromAddress}
        pinnedAttachments={pinnedAttachments}
        fromSelector={
          healthyConnections.length > 1 ? (
            <Select
              id="batch-email-from"
              aria-label="Send from"
              {...form.register('sendFromConnectionId')}
              className="w-full"
            >
              {healthyConnections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.email}
                </option>
              ))}
            </Select>
          ) : undefined
        }
      />
    </FormProvider>
  );
}
